import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Warning, X } from "@phosphor-icons/react";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { AppHeader } from "@/components/AppHeader";
import { CalendarEventDialog } from "@/components/CalendarEventDialog";
import { CalendarEventDetail } from "@/components/CalendarEventDetail";
import { CalendarToolbar } from "@/components/calendar/CalendarToolbar";
import { CalendarEventChip } from "@/components/calendar/CalendarEventChip";
import { iso, openPath } from "@/lib/calendarConstants";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtTime12 } from "@/lib/scheduleWindows";

export default function CalendarView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectFilter = searchParams.get("project");
  const [projectName, setProjectName] = useState("");
  const [conflicts, setConflicts] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [overDay, setOverDay] = useState(null);
  const [conf, setConf] = useState("all");
  const [cutoff, setCutoff] = useState("17:00");
  const [showArchived, setShowArchived] = useState(false);
  const [showConflicts, setShowConflicts] = useState(() => {
    try {
      return localStorage.getItem("brioni.cal.conflicts") !== "0";
    } catch {
      return true;
    }
  });
  const [eventDlg, setEventDlg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [stageFilter, setStageFilter] = useState("All");
  const [hideCompleted, setHideCompleted] = useState(false);

  const range = useMemo(() => {
    if (view === "day") return { from: cursor, to: cursor };
    if (view === "week")
      return {
        from: startOfWeek(cursor, { weekStartsOn: 1 }),
        to: endOfWeek(cursor, { weekStartsOn: 1 }),
      };
    return {
      from: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
      to: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
    };
  }, [view, cursor]);

  const days = useMemo(() => {
    const out = [];
    for (let d = range.from; d <= range.to; d = addDays(d, 1)) out.push(d);
    return out;
  }, [range]);

  const load = useCallback(async () => {
    const { data } = await api.get("/calendar", {
      params: { start: iso(range.from), end: iso(range.to), include_archived: showArchived },
    });
    setEvents(data.events);
  }, [range, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!projectFilter) {
      setProjectName("");
      return;
    }
    api
      .get(`/projects/${projectFilter}`)
      .then(({ data }) => setProjectName(data.name || data.code || ""))
      .catch(() => setProjectName(""));
  }, [projectFilter]);

  useEffect(() => {
    if (!showConflicts) {
      setConflicts([]);
      return;
    }
    api
      .get("/schedule/crew-conflicts")
      .then(({ data }) => setConflicts(data.enabled ? data.conflicts || [] : []))
      .catch(() => setConflicts([]));
  }, [showConflicts]);

  const toggleConflicts = () =>
    setShowConflicts((s) => {
      const n = !s;
      try {
        localStorage.setItem("brioni.cal.conflicts", n ? "1" : "0");
      } catch {
        /* ignore */
      }
      return n;
    });

  const resolveConflict = async (stop) => {
    try {
      const { data } = await api.post("/schedule/resolve-conflict", {
        job_id: stop.job_id,
        kind: stop.kind,
      });
      toast.success(`${stop.code} · ${stop.kind} moved to ${data.date} (${data.window})`);
      const [cf, ev] = await Promise.all([
        api.get("/schedule/crew-conflicts"),
        api.get("/calendar", {
          params: { start: iso(range.from), end: iso(range.to) },
        }),
      ]);
      setConflicts(cf.data.enabled ? cf.data.conflicts || [] : []);
      setEvents(ev.data.events);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not move this stop");
    }
  };

  const clearProject = () => {
    searchParams.delete("project");
    setSearchParams(searchParams, { replace: true });
  };

  const step = (dir) =>
    setCursor((c) =>
      view === "month" ? addMonths(c, dir) : addDays(c, dir * (view === "week" ? 7 : 1)),
    );

  const drop = async (day) => {
    setOverDay(null);
    const e = dragged;
    setDragged(null);
    if (!e || e.date === iso(day)) return;
    try {
      await api.patch("/calendar/move", {
        entity: e.entity,
        id: e.id,
        kind: e.kind,
        date: iso(day),
      });
      toast.success(`${e.code} ${e.kind} moved to ${format(day, "EEE, MMM d")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not move");
    }
  };

  const chip = (e) => (
    <CalendarEventChip
      key={`${e.entity}-${e.id}-${e.kind}`}
      e={e}
      onDragStart={() => setDragged(e)}
      onDragEnd={() => setDragged(null)}
      onClick={() => (e.entity === "custom" ? setEventDlg({ event: e }) : setDetail(e))}
    />
  );

  const isAppt = (e) => !!e.job_id && ["Template", "Fabrication", "Install"].includes(e.kind);
  const shown = events.filter((e) => {
    if (projectFilter && e.project_id !== projectFilter) return false;
    if (stageFilter !== "All" && e.stage !== stageFilter) return false;
    if (hideCompleted && (e.done || e.stage === "Complete" || e.stage === "Completed")) return false;
    return conf === "all"
      ? true
      : conf === "confirmed"
        ? e.confirmed
        : isAppt(e) && !e.confirmed;
  });
  const confirmedCount = events.filter((e) => e.confirmed).length;
  const unconfirmedCount = events.filter((e) => isAppt(e) && !e.confirmed).length;
  const byDay = (day) => shown.filter((e) => e.date === iso(day));

  const cutoffMin = (() => {
    const [h, m] = (cutoff || "17:00").split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  })();
  // Rough finish estimate per crew (used before a route is planned): earliest start,
  // then ~30m drive between stops + each stop's on-site time; falls back to applied ETAs.
  const dayRunsLate = (day) => {
    const evs = byDay(day).filter(
      (e) =>
        (e.entity === "job" || e.entity === "service") &&
        (e.address || "").trim() &&
        (e.crew || "").trim(),
    );
    if (!evs.length) return false;
    const groups = {};
    evs.forEach((e) => {
      (groups[e.crew] = groups[e.crew] || []).push(e);
    });
    return Object.values(groups).some((list) => {
      const times = list
        .map((e) => e.time)
        .filter(Boolean)
        .map((s) => {
          const [h, m] = s.split(":").map(Number);
          return (h || 0) * 60 + (m || 0);
        });
      let t = times.length ? Math.min(...times) : 8 * 60;
      list.forEach((e, i) => {
        if (e.route_eta) {
          const d = new Date(e.route_eta);
          t = d.getHours() * 60 + d.getMinutes();
        } else if (i > 0) {
          t += 30;
        }
        t += e.service_min > 0 ? e.service_min : 60;
      });
      return t > cutoffMin;
    });
  };
  const lateBadge = (day) =>
    dayRunsLate(day) ? (
      <span
        data-testid={`calendar-late-${iso(day)}`}
        title={`A crew's day runs past ${fmtTime12(cutoff)}`}
        className="inline-flex items-center gap-1 rounded-full bg-red-500/20 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-widest text-red-300"
      >
        <Warning size={9} weight="fill" /> Late
      </span>
    ) : null;

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Calendar" />
      <main className="relative z-10 pb-28 pt-6">
        <CalendarToolbar
          view={view}
          setView={setView}
          conf={conf}
          setConf={setConf}
          confirmedCount={confirmedCount}
          unconfirmedCount={unconfirmedCount}
          stageFilter={stageFilter}
          setStageFilter={setStageFilter}
          hideCompleted={hideCompleted}
          setHideCompleted={setHideCompleted}
          cutoff={cutoff}
          setCutoff={setCutoff}
          showArchived={showArchived}
          setShowArchived={setShowArchived}
          showConflicts={showConflicts}
          onToggleConflicts={toggleConflicts}
          onAddEvent={() => setEventDlg({ event: null })}
          onPlanRoutes={() => navigate("/route-planner")}
          onPrev={() => step(-1)}
          onToday={() => setCursor(new Date())}
          onNext={() => step(1)}
          cursor={cursor}
          range={range}
          eventsLength={events.length}
        />

        {projectFilter && (
          <div
            data-testid="calendar-project-filter"
            className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-4 py-3"
          >
            <span className="text-[11px] font-bold uppercase tracking-widest text-indigo-200">
              Filtered to project
            </span>
            <span className="min-w-0 truncate text-sm font-semibold text-white">
              {projectName || projectFilter}
            </span>
            <button
              type="button"
              data-testid="calendar-project-filter-clear"
              onClick={clearProject}
              className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-900 px-3 text-[10px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:border-indigo-400 hover:text-white"
            >
              <X size={12} weight="bold" /> Clear
            </button>
          </div>
        )}

        {showConflicts && conflicts.length > 0 && (
          <div
            data-testid="calendar-crew-conflicts"
            className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3"
          >
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-red-300">
              <Warning size={13} weight="fill" /> {conflicts.length} crew
              double-booking{conflicts.length > 1 ? "s" : ""}
            </div>
            <div className="mt-2 space-y-1.5">
              {conflicts.map((c, i) => (
                <div
                  key={i}
                  data-testid={`calendar-conflict-${i}`}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-zinc-300"
                >
                  <span className="font-semibold text-white">{c.crew}</span>
                  <span className="text-zinc-500">·</span>
                  <span className="font-mono-data">{c.date}</span>
                  <span className="rounded-full border border-zinc-700 px-1.5 text-[10px] uppercase tracking-widest text-zinc-400">
                    {c.window}
                  </span>
                  <span className="text-zinc-500">—</span>
                  {c.stops.map((s, j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={() => navigate(`/projects/${s.job_id}?tab=field`)}
                      className="rounded-md border border-red-500/30 px-1.5 py-0.5 font-mono-data text-[11px] text-red-200 transition-colors duration-150 hover:bg-red-500/20"
                    >
                      {s.code} · {s.kind}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid={`calendar-conflict-resolve-${i}`}
                    onClick={() => resolveConflict(c.stops[c.stops.length - 1])}
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
                  >
                    Move {c.stops[c.stops.length - 1].code} → next slot
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "month" && (
          <div
            className="mt-6 hidden grid-cols-7 gap-2 sm:grid"
            data-testid="calendar-month"
          >
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div
                key={d}
                className="px-2 text-[11px] uppercase tracking-widest text-zinc-600"
              >
                {d}
              </div>
            ))}
            {days.map((day) => (
              <div
                key={iso(day)}
                data-testid={`calendar-day-${iso(day)}`}
                onDragOver={(ev) => {
                  ev.preventDefault();
                  setOverDay(iso(day));
                }}
                onDragLeave={() => setOverDay(null)}
                onDrop={() => drop(day)}
                className={`calendar-day-cell min-h-28 rounded-lg border p-2 ${
                  overDay === iso(day)
                    ? "border-yellow-500"
                    : isSameMonth(day, cursor)
                      ? "border-zinc-800"
                      : "border-zinc-800/50 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-data text-xs text-zinc-500">
                    {format(day, "d")}
                  </span>
                  <span className="flex items-center gap-1">
                    {lateBadge(day)}
                    {iso(day) === iso(new Date()) && (
                      <span className="cal-today-pill rounded-full bg-indigo-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                        Today
                      </span>
                    )}
                  </span>
                </div>
                <div className="mt-2 space-y-1.5">{byDay(day).map(chip)}</div>
              </div>
            ))}
          </div>
        )}

        {(view === "week" || view === "day") && (
          <div
            className={`mt-6 hidden gap-2 sm:grid ${view === "week" ? "grid-cols-7" : "grid-cols-1"}`}
            data-testid={`calendar-${view}`}
          >
            {days.map((day) => (
              <div
                key={iso(day)}
                data-testid={`calendar-day-${iso(day)}`}
                onDragOver={(ev) => {
                  ev.preventDefault();
                  setOverDay(iso(day));
                }}
                onDragLeave={() => setOverDay(null)}
                onDrop={() => drop(day)}
                className={`calendar-day-cell min-h-[18rem] rounded-lg border p-3 ${
                  overDay === iso(day) ? "border-indigo-500" : "border-zinc-800"
                }`}
              >
                <div className="flex items-center justify-between pb-2">
                  <span className="text-xs font-semibold uppercase tracking-widest text-zinc-300">
                    {format(day, "EEE")}
                  </span>
                  <span className="flex items-center gap-1">
                    {lateBadge(day)}
                    <span className="font-mono-data text-xs text-zinc-500">
                      {format(day, "MM-dd")}
                    </span>
                  </span>
                </div>
                <div className="space-y-2">
                  {byDay(day).length === 0 && (
                    <p className="rounded-md border border-dashed border-zinc-800 p-3 text-center text-[11px] text-zinc-600">
                      Clear
                    </p>
                  )}
                  {byDay(day).map(chip)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* mobile agenda */}
        <div className="mt-6 space-y-4 sm:hidden" data-testid="calendar-agenda">
          {days
            .filter((d) => byDay(d).length > 0)
            .map((day) => (
              <div
                key={iso(day)}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-3"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-zinc-400">
                  {format(day, "EEE, MMM d")}
                  {lateBadge(day)}
                </div>
                <div className="mt-2 space-y-2">{byDay(day).map(chip)}</div>
              </div>
            ))}
          {days.every((d) => byDay(d).length === 0) && (
            <p className="rounded-lg border border-dashed border-zinc-800 p-8 text-center text-sm text-zinc-500">
              Nothing scheduled in this period.
            </p>
          )}
        </div>

        <CalendarEventDialog
          open={!!eventDlg}
          onOpenChange={(o) => !o && setEventDlg(null)}
          event={eventDlg?.event || null}
          defaultDate={iso(cursor)}
          onSaved={() => {
            setEventDlg(null);
            load();
          }}
        />

        <CalendarEventDetail
          open={!!detail}
          onOpenChange={(o) => !o && setDetail(null)}
          event={detail}
          onOpenRecord={() => {
            if (detail) {
              navigate(openPath(detail));
              setDetail(null);
            }
          }}
        />
      </main>
    </div>
  );
}
